import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";
import { trackEvent } from "@/lib/analytics";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

/**
 * Texte de la Garantie Partenaire 90 — formulation volontairement isolée
 * pour permettre une validation juridique avant la mise en ligne.
 */
export const guaranteeCopy = {
  eyebrow: "Garantie Partenaire 90",
  title: "90 jours. Un objectif.",
  body: "Au lancement, nous définissons ensemble un objectif précis et mesurable pour les 90 premiers jours.",
  statement: "Nos honoraires s'arrêtent. Notre travail continue.",
  conditions:
    "La Garantie Partenaire 90 est réservée aux entreprises admissibles qui respectent les budgets, les délais d'approbation, le processus de suivi et les engagements convenus au lancement. Le budget média, les logiciels, les frais externes, les tournages et la production de nouveaux créatifs demeurent à la charge du client.",
  cta: "Voir si votre entreprise est admissible",
};

const steps = [
  { num: "01", title: "Comprendre", text: "Nous analysons votre offre, votre marketing et votre processus de vente pour voir où la croissance bloque." },
  { num: "02", title: "Prioriser", text: "Nous choisissons les leviers à plus fort impact au lieu d'ajouter des tactiques." },
  { num: "03", title: "Construire", text: "Nous produisons les actifs, structurons les campagnes et connectons le parcours du prospect." },
  { num: "04", title: "Optimiser", text: "Nous suivons les leads, les rendez-vous et les ventes pour concentrer les efforts sur ce qui fonctionne." },
];

const fadeUp = {
  initial: { opacity: 0, y: 18 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.4 } as const,
};

const GuaranteeV2 = () => (
  <section aria-labelledby="garantie-titre" className="relative overflow-hidden py-14 md:py-24">
    <div
      aria-hidden="true"
      className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_45%_55%_at_78%_18%,hsl(var(--primary)/0.06)_0%,transparent_72%)]"
    />
    <div className="relative mx-auto max-w-[1280px] px-5 md:max-w-[1180px] md:px-8">
      {/* NIVEAU 1 — label */}
      <motion.p
        {...fadeUp}
        transition={{ duration: 0.5 }}
        className="text-[11px] font-medium uppercase tracking-[0.28em] text-primary"
      >
        {guaranteeCopy.eyebrow}
      </motion.p>

      {/* NIVEAU 2 — impact visuel : le 90 + texte solidaire */}
      <motion.div
        {...fadeUp}
        transition={{ duration: 0.6, delay: 0.05 }}
        className="mt-5 flex flex-col gap-2 md:mt-6 md:flex-row md:items-end md:gap-8"
      >
        <span
          aria-hidden="true"
          className="block select-none font-semibold leading-[0.78] tracking-[-0.05em] text-foreground text-[4.75rem] sm:text-[10rem] md:text-[13rem] lg:text-[15rem]"
        >
          90
        </span>
        <div className="md:pb-4">
          <h2
            id="garantie-titre"
            className="max-w-[18ch] text-xl font-normal leading-[1.15] tracking-tight text-muted-foreground md:max-w-[15ch] md:text-4xl"
          >
            <span className="sr-only">Garantie Partenaire 90 — </span>
            jours. <span className="text-foreground">Un objectif défini ensemble.</span>
          </h2>
          <motion.p
            {...fadeUp}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="mt-4 max-w-md text-sm leading-[1.55] text-muted-foreground md:mt-4 md:max-w-[42ch] md:text-[0.95rem]"
          >
            {guaranteeCopy.body}
          </motion.p>
        </div>
      </motion.div>

      {/* NIVEAU 3 — promesse */}
      <motion.div
        {...fadeUp}
        transition={{ duration: 0.6, delay: 0.1 }}
        className="mt-8 border-t border-border/50 pt-7 md:mt-14 md:pt-12"
      >
        <p className="text-[11px] font-medium uppercase tracking-[0.22em] text-muted-foreground/70">
          S'il n'est pas atteint :
        </p>
        <p className="mt-4 max-w-4xl text-[1.85rem] font-semibold leading-[1.08] tracking-tight text-foreground sm:text-4xl md:mt-6 md:text-6xl lg:text-7xl">
          Nos honoraires s'arrêtent.
          <br />
          <span className="text-primary">Notre travail continue.</span>
        </p>
      </motion.div>

      {/* CTA + modalités — même groupe */}
      <motion.div
        {...fadeUp}
        transition={{ duration: 0.5, delay: 0.1 }}
        className="mt-7 flex flex-wrap items-center gap-x-8 gap-y-5 md:mt-8"
      >
        <Button
          variant="outline"
          onClick={() => {
            trackEvent("primary_cta_clicked", { location: "garantie_partenaire_90" });
            document.querySelector("#formulaire")?.scrollIntoView({ behavior: "smooth", block: "start" });
          }}
          className="group w-full rounded-full border-border bg-transparent px-6 py-5 text-sm font-medium text-foreground hover:border-primary/60 hover:bg-transparent hover:text-primary sm:w-auto md:border-primary/40 md:bg-primary/[0.06] md:hover:bg-primary/10"
        >
          {guaranteeCopy.cta}
          <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
        </Button>

        <Accordion type="single" collapsible className="w-full max-w-xl md:w-auto md:max-w-[34rem] md:flex-none">
          <AccordionItem value="conditions" className="border-none">
            <AccordionTrigger className="py-0 text-xs font-normal text-muted-foreground/70 hover:text-muted-foreground">
              Modalités de la garantie
            </AccordionTrigger>
            <AccordionContent className="pt-4 text-xs leading-relaxed text-muted-foreground/60">
              {guaranteeCopy.conditions}
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </motion.div>


      <ol className="mt-12 grid gap-x-8 gap-y-6 border-t border-border/40 pt-8 md:mt-28 md:gap-y-8 md:pt-10 md:grid-cols-4">
        {steps.map((step, i) => (
          <motion.li
            key={step.num}
            initial={{ opacity: 0, y: 14 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.45, delay: i * 0.06 }}
            className="relative border-l border-border/50 pl-5 md:border-l-0 md:pl-0"
          >
            <span
              aria-hidden="true"
              className="absolute -left-[3.5px] top-[7px] h-1.5 w-1.5 rounded-full bg-primary md:hidden"
            />
            <span className="font-mono text-[11px] tracking-[0.2em] text-primary md:text-xs md:tracking-normal">{step.num}</span>
            <h3 className="mt-1 text-base font-medium text-foreground md:mt-2">{step.title}</h3>
            <p className="mt-1.5 text-sm leading-[1.5] text-muted-foreground/80 md:mt-2">{step.text}</p>
          </motion.li>
        ))}
      </ol>
    </div>
  </section>
);

export default GuaranteeV2;
